import { JWMButton } from "@components/JWMButton/JWMButton";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { JWMLink } from "@components/JWMLink/JWMLink";
import { CardFooter } from "@jmc/core/src/components/CardFooter";
import { GatsbyImageCard } from "@jmc/core/src/components/GatsbyImageCard/index";
import { ImageQueryResponse } from "@jmc/core/src/types/ImageQueryReponse";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import {
    Card,
    HorizontalAlignment,
    ImagePlacement,
    Padding,
} from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { default as getServicePageUrl } from "@utils/getServicePageUrl";
import { ServiceLink } from "components/JWMButton/JWMButton";
import React from "react";

import JWMCardStyleWrapper from "./JWMCardStyleWrapper/JWMCardStyleWrapper";

interface Props {
    button?: {
        buttonProps: React.ComponentProps<typeof Button>;
        link: { anchor_id: string; external: boolean; url: string; url_prefix: string; title: string };
        text: string;
        serviceLink?: ServiceLink;
    }[];
    id: string;
    image?: ImageQueryResponse;
    video?: JSX.Element;
    title?: string;
    description?: string;
    icon?: string;
    fullBackground?: boolean;
    backgroundColor?: string;
    textColor?: string;
    titleColor?: string;
    linkColor?: string;
    specialties?: string[];
    dropShadow?: boolean;
    footer?: JSX.Element | JSX.Element[];
    hasOverlay?: boolean;
    horizontalAlignment?: HorizontalAlignment;
    imagePlacement?: ImagePlacement;
    padding?: Padding;
    placement?: string;
    loading?: "eager" | "lazy";
    sectionLinkColor?: string;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    isFullCardClickable?: boolean;
    children?: JSX.Element;
}

export const JWMCard = (props: Props): JSX.Element => {
    const {
        button = null,
        id,
        image,
        title,
        description = null,
        icon,
        fullBackground = false,
        backgroundColor = undefined,
        titleColor = "--text-primary",
        textColor = "--text-light",
        linkColor = "--text-link",
        dropShadow = true,
        hasOverlay = false,
        placement = "none",
        loading = "lazy",
        sectionLinkColor = "primary",
        siteSpecialty,
        isFullCardClickable = true, // TODO: We don't need this prop after rebranding
        children,
    } = props;

    const { jnjFullBranded } = useJnjBranding();
    const formattedTitle = <JWMHtml showGlossary={false}>{title}</JWMHtml>;
    const finalBackgroundColor = backgroundColor || (jnjFullBranded ? "--transparent" : "--white");

    const cardProps = { ...props };
    delete cardProps.titleColor;
    delete cardProps.textColor;
    delete cardProps.linkColor;
    delete cardProps.backgroundColor;
    delete cardProps.siteSpecialty;
    delete cardProps.sectionLinkColor;
    delete cardProps.children;

    if (button == null) {
        return (
            <JWMCardStyleWrapper
                backgroundColor={fullBackground ? "--transparent" : finalBackgroundColor}
                titleColor={titleColor}
                textColor={textColor}
                linkColor={linkColor}
                sectionLinkColor={sectionLinkColor}
            >
                <GatsbyImageCard
                    {...cardProps}
                    title={formattedTitle}
                    hasOverlay={hasOverlay}
                    backgroundStyle={{ borderRadius: "var(--card-border-radius)", overflow: "hidden", height: "100%" }}
                    loading={loading}
                    isTransparent={finalBackgroundColor == "--transparent"}
                >
                    <>
                        {(description || !jnjFullBranded) && (
                            <JWMHtml color="inherit" siteSpecialty={siteSpecialty}>
                                {description}
                            </JWMHtml>
                        )}
                        {children}
                    </>
                </GatsbyImageCard>
            </JWMCardStyleWrapper>
        );
    } else {
        const theButton = button[button.length - 1];
        const buttonProps = theButton?.buttonProps;

        let finalUrlPrefix = theButton?.link?.url_prefix;
        let finalAnchorId = theButton?.link?.anchor_id;
        let finalUrl = theButton?.link?.url;

        if (theButton?.serviceLink?.servicePage) {
            finalUrlPrefix = "";
            finalAnchorId = "";
            finalUrl = getServicePageUrl(theButton?.serviceLink?.servicePage, theButton?.serviceLink?.specialty?.title);
        }

        const ctaIcon = jnjFullBranded ? <Card.CTAIconArrow /> : undefined;

        return (
            <JWMCardStyleWrapper
                backgroundColor={fullBackground ? "--transparent" : finalBackgroundColor}
                titleColor={titleColor}
                textColor={textColor}
                linkColor={linkColor}
                sectionLinkColor={sectionLinkColor}
            >
                <GatsbyImageCard
                    {...cardProps}
                    data-test-id={`Card.${id}`}
                    title={formattedTitle}
                    fullBackground={fullBackground}
                    hasOverlay={hasOverlay}
                    dropShadow={dropShadow}
                    icon={icon}
                    image={image}
                    isClickable={true}
                    backgroundStyle={{
                        borderRadius: "var(--card-border-radius)",
                        overflow: "hidden",
                        height: "100%",
                    }}
                    loading={loading}
                    isTransparent={finalBackgroundColor == "--transparent"}
                    footer={
                        <CardFooter>
                            {props?.footer}
                            <JWMLink
                                ariaLabel={null}
                                url={finalUrl}
                                url_prefix={finalUrlPrefix}
                                external={theButton?.link?.external}
                                anchor_id={finalAnchorId}
                                shouldTrackLinkClick={false}
                                fullWidth={false}
                                expandHitbox={jnjFullBranded ? false : isFullCardClickable}
                            >
                                <JWMButton
                                    disableLink={true}
                                    url={theButton?.link?.url}
                                    url_prefix={theButton?.link?.url_prefix}
                                    external={theButton?.link?.external}
                                    anchor_id={theButton?.link?.anchor_id}
                                    buttonProps={buttonProps}
                                    placement={placement}
                                    text={theButton?.text}
                                    serviceLink={theButton.serviceLink}
                                    accessoryRight={ctaIcon}
                                />
                            </JWMLink>
                        </CardFooter>
                    }
                >
                    <>
                        {(description || !jnjFullBranded) && (
                            <Typography color="primary">
                                <JWMHtml color="inherit" siteSpecialty={siteSpecialty}>
                                    {description}
                                </JWMHtml>
                            </Typography>
                        )}
                        {children}
                    </>
                </GatsbyImageCard>
            </JWMCardStyleWrapper>
        );
    }
};

export default JWMCard;
